import {
  Component,
  ElementRef,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import Hls from 'hls.js';

import { ApiFilesV2Service } from '../../../services/api/api-files-v2.service';
import { ApiFilesV3Service } from '../../../services/api/api-files-v3.service';
import { AudioItem } from '../../../common/models/event/audio-item';
import { CommonHelper } from '../../../common/helpers/common.helper';
import { EventService } from '../../../services/event.service';
import { EventStatus } from '../../../common/enums/event/event-status';
import { Language } from '../../../common/enums/language/language';
import { SessionVideoOptions } from '../../../common/models/event/session-video-options';
import { VideoSrc } from '../../../common/models/common/video/video-src';
import { i18nService } from '../../../services/i18n.service';
import { Subscription } from 'rxjs';
import { CommonUrlBasedService } from '../../../services/common.url-based.service';

@Component({
  selector: 'mec-video-player',
  imports: [CommonModule],
  templateUrl: './video-player.component.html',
  styleUrls: [
    './video-player.component.scss',
    './custom-select.component.scss',
  ],
})
export class VideoPlayerComponent implements OnInit, OnChanges, OnDestroy {
  @Input() sourceVideo: VideoSrc;
  @Input() coverVideoUrl: string;
  @Input() sessionOptions: SessionVideoOptions;
  public EventStatusEnum = EventStatus;

  public options: any = {
    fluid: true,
    aspectRatio: '16:9',
    autoplay: false,
  };

  @ViewChild('target', { static: true })
  target: ElementRef;

  public player: any;
  private hls?: Hls;
  private subtitleTracks: HTMLTrackElement[] = [];
  public isAtLiveEdge = false;
  public liveLatency = 0;
  private readonly LIVE_ENTER_THRESHOLD_SEC = 1.5; // become LIVE when <= 1.5s behind
  private readonly LIVE_EXIT_THRESHOLD_SEC = 3.0; // leave LIVE when > 3s behind
  private _updateLiveStateBound = () => this._updateLiveState();
  public isPlaying = false;
  public muted = false;
  public isFullscreen = false;
  public volume = 1;
  public levels: any[] = [];
  public selectedQuality: 'auto' | number = 'auto';

  // Custom select properties
  public isQualityDropdownOpen = false;

  // Mobile-specific properties
  public controlsVisible = true;
  private _controlsTimeout: any;
  private _isMobile = false;

  public subscription: Subscription;

  constructor(
    private _apiFilesV2Service: ApiFilesV2Service,
    private _apiFilesV3Service: ApiFilesV3Service,
    private _elementRef: ElementRef,
    private _eventService: EventService,
    private _i18nService: i18nService,
    private _commonService: CommonUrlBasedService
  ) {
    this.subscription = this._eventService.playerUpdateSubject.subscribe(
      (audioItem) => {
        this.target.nativeElement.scrollIntoView({
          block: 'center',
          behavior: 'smooth',
        });
        this.setItemVideoPlayer(audioItem);
      }
    );

    this._isMobile = this._isMobileDevice();

    document.addEventListener('fullscreenchange', () => {
      this.isFullscreen = !!document.fullscreenElement;
      if (this.isFullscreen && this._isMobile) {
        this._showControlsTemporarily();
      }
    });

    window.addEventListener('orientationchange', () => {
      // Small delay to allow orientation change to complete
      setTimeout(() => {
        this._handleOrientationChange();
      }, 100);
    });
  }

  public ngOnChanges(changes: SimpleChanges): void {
    this._teardown();

    if (this.sourceVideo) {
      this._handleDefaultVideo();
      return;
    }

    if (this.sessionOptions?.eventStatus === EventStatus.ON_DEMAND) {
      this._handleOnDemandPlayer();
    } else if (this.sessionOptions?.eventStatus === EventStatus.LIVE) {
      this._handleLivePlayer();
    }
  }

  public ngOnDestroy(): void {
    this._teardown();
    this.subscription?.unsubscribe();
    if (this.player) {
      this._destroySubtitles();
      this.player.dispose();
    }
  }

  private _onPlayerReady(): void {}

  public ngOnInit(): void {}

  private _destroySubtitles() {
    const video = this.target?.nativeElement as HTMLVideoElement | undefined;
    if (!video) return;
    this.subtitleTracks.forEach((t) => {
      try {
        video.removeChild(t);
      } catch {}
    });
    this.subtitleTracks = [];
  }

  private _handleDefaultVideo(): void {
    const video = this.target.nativeElement as HTMLVideoElement;
    const s: any = Array.isArray(this.sourceVideo)
      ? this.sourceVideo[0]
      : this.sourceVideo;
    const src = typeof s === 'string' ? s : s?.src;
    const type = typeof s === 'string' ? undefined : s?.type;

    video.poster = this.coverVideoUrl || '';
    if (src?.endsWith('.m3u8')) {
      this._attachHls(src);
      // after attaching HLS, request play in case autoplay flag didn't trigger
      video.addEventListener('loadedmetadata', () => {
        if (video.paused) {
          void video.play().catch(() => {});
        }
      });
    } else {
      video.src = src || '';
      video.load();
    }
  }

  private _handleLivePlayer(): void {
    const liveSrc = this.sessionOptions?.eventLiveUrl as any;
    const src = typeof liveSrc === 'string' ? liveSrc : liveSrc?.src;
    const video = this.target.nativeElement as HTMLVideoElement;
    video.autoplay = true;
    video.playsInline = true;
    if (!src) return;
    this._attachHls(src);
    // when using HLS ensure it begins playing if not already
    video.addEventListener('loadedmetadata', () => {
      if (video.paused) {
        void video.play().catch(() => {});
      }
    });
    video.addEventListener('timeupdate', this._updateLiveStateBound);
    video.addEventListener('progress', this._updateLiveStateBound);
    video.addEventListener('play', () => (this.isPlaying = true));
    video.addEventListener('pause', () => (this.isPlaying = false));
    this.muted = video.muted;
    this.volume = video.volume;
  }

  private _handleOnDemandPlayer(): void {
    const defaultAudioItem =
      this.sessionOptions.audios.items.find(
        (item) => item.language === this._i18nService.language
      ) || this.sessionOptions.audios.items[0];

    const video = this.target.nativeElement as HTMLVideoElement;
    const srcObj = this._getVODSourceVideo(defaultAudioItem.videoUrl);
    video.src = srcObj.src;
    video.poster = this._getVODPosterImage(defaultAudioItem.videoUrl);
    video.onloadedmetadata = () => {
      this._onPlayerReady();
      this._handleSubtitles();
    };
    video.addEventListener('play', () => (this.isPlaying = true));
    video.addEventListener('pause', () => (this.isPlaying = false));
    this.muted = video.muted;
    this.volume = video.volume;
  }

  private _handleLanguageOptions(): void {
    // No-op: Video.js custom menu removed; use app-level UI if needed.
  }

  private async _handleSubtitles(): Promise<void> {
    this._destroySubtitles();
    const video = this.target.nativeElement as HTMLVideoElement;
    const source = video.currentSrc || video.src;
    if (!source) return;

    try {
      const urlExtracted = CommonHelper.extractFileUrl(source);
      const subtitles = await this._apiFilesV3Service.getFileSubtitlesByUrl(
        urlExtracted
      );

      if (!subtitles) return;

      subtitles.forEach((subtitle) => {
        const track = document.createElement('track');
        track.kind = 'subtitles';
        track.label = subtitle.description;
        track.srclang = subtitle.language;
        track.src = subtitle.url;
        if (subtitle.language === this._i18nService.language) {
          track.default = true;
        }
        video.appendChild(track);
        this.subtitleTracks.push(track);
      });
    } catch (_handleSubtitlesError) {
      console.error(_handleSubtitlesError);
    }
  }

  private _getVODSourceVideo(videoUrl: string): { src: string; type: string } {
    const source = {
      src: videoUrl + '?quality=720&format=mp4',
      type: 'video/mp4',
    };

    return source;
  }

  private _getVODPosterImage(imagePosterUrl?: string): string {
    imagePosterUrl = imagePosterUrl + '?format=thumbnail&width=1024';

    return imagePosterUrl;
  }

  public selectSubtitle(language: Language) {
    const video = this.target?.nativeElement as HTMLVideoElement | undefined;
    if (!video) return;
    const textTracks = video.textTracks;

    if (!textTracks || textTracks.length === 0) return;

    for (let i = 0; i < textTracks.length; i++) {
      const track = textTracks[i];
      if (track.kind === 'subtitles') {
        track.mode = !language
          ? 'disabled'
          : track.language === (language as unknown as string)
          ? 'showing'
          : 'disabled';
      }
    }
  }

  public setItemVideoPlayer(audioItem: AudioItem): void {
    const video = this.target.nativeElement as HTMLVideoElement;
    const srcObj = this._getVODSourceVideo(audioItem.videoUrl);
    video.src = srcObj.src;
    video.poster = this._getVODPosterImage(audioItem.videoUrl);
    const currentTime = audioItem.currentTime || 0;
    video.currentTime = currentTime;
    void video.play().catch(() => {});
  }

  public clickLanguage(language: string): void {
    const audioItem = this.sessionOptions.audios.items.find(
      (item) => item.language === language
    );
    const video = this.target.nativeElement as HTMLVideoElement;
    audioItem.currentTime = video.currentTime;
    this.setItemVideoPlayer(audioItem);
  }

  private _attachHls(src: string) {
    const video = this.target.nativeElement as HTMLVideoElement;
    if (Hls.isSupported()) {
      this.hls = new Hls({
        lowLatencyMode: true,
        liveSyncDurationCount: 6,
        liveMaxLatencyDurationCount: 12,
        maxLiveSyncPlaybackRate: 1.0,
        maxBufferLength: 30,
        maxMaxBufferLength: 60,
        maxBufferSize: 60 * 1000 * 1000,
        backBufferLength: 30,
        enableWorker: true,
      });
      this.hls.attachMedia(video);
      this.hls.on(Hls.Events.MEDIA_ATTACHED, () => {
        this.hls?.loadSource(src);
        this._updateLiveState();
      });
      this.hls.on(Hls.Events.MANIFEST_PARSED, (_evt, data: any) => {
        // Filter out duplicate levels based on height to avoid repeated resolutions
        const rawLevels = this.hls?.levels || [];
        const seenHeights = new Set<number>();
        this.levels = rawLevels
          .map((level, originalIndex) => ({ ...level, originalIndex }))
          .filter((level) => {
            if (level.height && !seenHeights.has(level.height)) {
              seenHeights.add(level.height);
              return true;
            }
            return false;
          });
        this.selectedQuality = this.hls?.autoLevelEnabled
          ? 'auto'
          : this.levels.findIndex(
              (level) => level.originalIndex === this.hls?.currentLevel
            ) ?? 'auto';
      });
      // this.hls.on(Hls.Events.LEVEL_SWITCHED, () => this._updateLiveState());
      // this.hls.on(Hls.Events.FRAG_BUFFERED, () => this._updateLiveState());
      this.hls.on(Hls.Events.LEVEL_SWITCHING, (event, data) => {
        // Prevent automatic level switching when manual quality is selected
        if (
          this.selectedQuality !== 'auto' &&
          data.level !== this.levels[this.selectedQuality]?.originalIndex
        ) {
          // Force stay at selected level
          data.level = this.levels[this.selectedQuality].originalIndex;
        }
      });
      // this.hls.on(Hls.Events.ERROR, (_e, data) => {
      //   if (data?.fatal) {
      //     switch (data.type) {
      //       case Hls.ErrorTypes.NETWORK_ERROR:
      //         this.hls?.startLoad();
      //         break;
      //       case Hls.ErrorTypes.MEDIA_ERROR:
      //         this.hls?.recoverMediaError();
      //         break;
      //       default:
      //         this.hls?.destroy();
      //         this.hls = undefined;
      //         break;
      //     }
      //   }
      // });
    } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
      video.src = src;
      video.load();
      this._updateLiveState();
    } else {
      // Fallback: let the browser try anyway
      video.src = src;
      video.load();
      this._updateLiveState();
    }
  }

  private _teardown() {
    this._destroySubtitles();
    if (this.hls) {
      try {
        this.hls.destroy();
      } catch {}
      this.hls = undefined;
    }
    const video = this.target?.nativeElement as HTMLVideoElement | undefined;
    if (video) {
      video.removeEventListener('timeupdate', this._updateLiveStateBound);
      video.removeEventListener('progress', this._updateLiveStateBound);
      video.removeEventListener('play', () => (this.isPlaying = true));
      video.removeEventListener('pause', () => (this.isPlaying = false));
      try {
        video.pause();
      } catch {}
      video.removeAttribute('src');
      try {
        video.load();
      } catch {}
    }
    this.isAtLiveEdge = false;
    this.liveLatency = 0;
  }

  private _updateLiveState(): void {
    try {
      const video = this.target?.nativeElement as HTMLVideoElement | undefined;
      if (!video) return;
      let liveEdge = Number.NaN;
      const anyHls = this.hls as any;
      if (anyHls && typeof anyHls.liveSyncPosition === 'number') {
        liveEdge = anyHls.liveSyncPosition as number;
      } else if (video.buffered && video.buffered.length) {
        liveEdge = video.buffered.end(video.buffered.length - 1);
      } else {
        liveEdge = video.currentTime;
      }
      const delay = Math.max(0, liveEdge - video.currentTime);
      this.liveLatency = delay;
      const wasLive = this.isAtLiveEdge;
      if (wasLive) {
        this.isAtLiveEdge = delay <= this.LIVE_EXIT_THRESHOLD_SEC;
      } else {
        this.isAtLiveEdge = delay <= this.LIVE_ENTER_THRESHOLD_SEC;
      }
    } catch {}
  }

  public jumpToLive(): void {
    const video = this.target?.nativeElement as HTMLVideoElement | undefined;
    if (!video) return;
    try {
      if (
        this.hls &&
        typeof (this.hls as any).seekToLivePosition === 'function'
      ) {
        (this.hls as any).seekToLivePosition();
      } else if (
        this.hls &&
        typeof (this.hls as any).liveSyncPosition === 'number'
      ) {
        video.currentTime = (this.hls as any).liveSyncPosition as number;
      } else {
        const buffered = video.buffered;
        if (buffered && buffered.length) {
          video.currentTime = buffered.end(buffered.length - 1) - 0.5;
        }
      }
      void video.play().catch(() => {});
    } catch (err) {
      console.error('jumpToLive error', err);
    }
  }

  public togglePlayPause(): void {
    const video = this.target?.nativeElement as HTMLVideoElement | undefined;
    if (!video) return;
    if (video.paused) {
      void video.play().catch(() => {});
    } else {
      video.pause();
    }
  }

  public toggleMute(): void {
    const video = this.target?.nativeElement as HTMLVideoElement | undefined;
    if (!video) return;
    video.muted = !video.muted;
    this.muted = video.muted;
  }

  public setVolume(event: Event): void {
    const input = (event.target || event.currentTarget) as
      | HTMLInputElement
      | null
      | undefined;
    if (!input) return;
    const parsed = parseFloat(input.value || '0');
    if (!Number.isFinite(parsed)) return;
    const v = Math.max(0, Math.min(1, parsed));
    const video = this.target?.nativeElement as HTMLVideoElement | undefined;
    if (!video) return;
    video.volume = v;
    this.volume = v;
    if (v > 0 && video.muted) {
      video.muted = false;
      this.muted = false;
    }
  }

  public selectQuality(value: 'auto' | number): void {
    if (!this.hls) return;
    if (value === 'auto') {
      this.hls.currentLevel = -1;
      (this.hls.config as any).abrEwmaDefaultEstimate = 5e5; // default
      this.selectedQuality = 'auto';
    } else {
      const idx =
        typeof value === 'number' ? value : parseInt(String(value), 10);
      if (!Number.isFinite(idx) || !this.levels[idx]) return;
      this.hls.currentLevel = this.levels[idx].originalIndex;
      (this.hls.config as any).abrEwmaDefaultEstimate = Number.MAX_VALUE; // disable ABR
      this.selectedQuality = idx;
    }
    this.isQualityDropdownOpen = false; // Close dropdown after selection
  }

  public toggleQualityDropdown(): void {
    this.isQualityDropdownOpen = !this.isQualityDropdownOpen;
  }

  public getSelectedQualityLabel(): string {
    if (this.selectedQuality === 'auto') {
      return 'Auto';
    }
    const level = this.levels[this.selectedQuality as number];
    return level
      ? level.height
        ? level.height + 'p'
        : level.name || 'Level ' + this.selectedQuality
      : 'Auto';
  }

  public get liveProgressPercent(): number {
    // Show delay as percentage of a 20s window
    const windowSec = 20;
    return Math.max(0, Math.min(100, (this.liveLatency / windowSec) * 100));
  }

  public get vodProgressPercent(): number {
    const video = this.target?.nativeElement as HTMLVideoElement | undefined;
    if (!video || !isFinite(video.duration) || video.duration <= 0) return 0;
    return (video.currentTime / video.duration) * 100;
  }

  public seekVod(event: MouseEvent): void {
    const video = this.target?.nativeElement as HTMLVideoElement | undefined;
    if (!video || !isFinite(video.duration) || video.duration <= 0) return;
    const el = event.currentTarget as HTMLElement;
    const rect = el.getBoundingClientRect();
    const percent = Math.max(
      0,
      Math.min(1, (event.clientX - rect.left) / rect.width)
    );
    video.currentTime = percent * video.duration;
  }

  public toggleFullscreen(): void {
    const host = (this._elementRef?.nativeElement as HTMLElement) ?? undefined;
    if (!host) return;
    const d = document as any;
    if (!d.fullscreenElement) {
      this.isFullscreen = true;
      host.requestFullscreen?.().catch((err) => {
        console.warn('Failed to enter fullscreen:', err);
        this.isFullscreen = false;
      });
      // Close any open mat-select dropdowns when entering fullscreen
      setTimeout(() => {
        this._closeOpenDropdowns();
      }, 100);
    } else {
      this.isFullscreen = false;
      d.exitFullscreen?.().catch((err: any) => {
        console.warn('Failed to exit fullscreen:', err);
      });
    }
  }

  private _handleOrientationChange(): void {
    // Force a reflow to ensure controls are properly positioned after orientation change
    const controls = this._elementRef?.nativeElement?.querySelector(
      '.video-player__controls'
    ) as HTMLElement;
    if (controls) {
      // Trigger reflow
      controls.style.display = 'none';
      controls.offsetHeight; // Trigger reflow
      controls.style.display = '';
    }
  }

  private _isMobileDevice(): boolean {
    if (this._commonService.isPlatformServer) {
      return false;
    }

    return (
      /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
        navigator.userAgent
      ) ||
      (window.innerWidth <= 768 && window.innerHeight <= 1024)
    );
  }

  private _showControlsTemporarily(): void {
    this.controlsVisible = true;
    if (this._controlsTimeout) {
      clearTimeout(this._controlsTimeout);
    }
    this._controlsTimeout = setTimeout(() => {
      this.controlsVisible = false;
    }, 3000); // Hide controls after 3 seconds
  }

  public onVideoTouch(event: TouchEvent): void {
    if (this._isMobile && !this.isFullscreen) {
      this._showControlsTemporarily();
    }
  }

  private _closeOpenDropdowns(): void {
    // Close any open mat-select dropdowns to prevent positioning issues in fullscreen
    const overlays = document.querySelectorAll(
      '.cdk-overlay-pane.mat-mdc-select-panel'
    );
    overlays.forEach((overlay) => {
      const backdrop = overlay.parentElement?.querySelector(
        '.cdk-overlay-backdrop'
      );
      if (backdrop) {
        (backdrop as HTMLElement).click(); // Simulate clicking the backdrop to close
      }
    });
  }

  public get hasHls(): boolean {
    return !!this.hls;
  }
}
